import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { fetchData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import CircularProgress from '@mui/material/CircularProgress';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';

// ── Design tokens ─────────────────────────────────────────────────────────────
const C = {
  accent: '#2dd4bf', accentDark: '#14b8a6',
  bg: '#f0f4f4', card: '#ffffff', cardBorder: 'rgba(0,0,0,0.06)',
  text: '#1a2e2e', muted: '#5a7a7a', shadow: '0 2px 10px rgba(0,0,0,0.07)',
  danger: '#ef4444',
};

const inp  = { border: `1px solid ${C.cardBorder}`, borderRadius: '7px', fontSize: '12px', height: '28px', width: '100%', padding: '0 8px', outline: 'none', backgroundColor: '#f0f4f4', boxSizing: 'border-box', fontFamily: 'inherit', cursor: 'default', color: C.text };
const inpR = { ...inp, textAlign: 'right' };
const card = { backgroundColor: C.card, borderRadius: '10px', boxShadow: C.shadow, border: `1px solid ${C.cardBorder}`, marginBottom: '10px', overflow: 'hidden' };
const cardHead = (color = C.accent) => ({ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 12px', borderBottom: `1px solid ${C.cardBorder}`, backgroundColor: `${color}18`, fontSize: '10px', fontWeight: 700, color: C.text, textTransform: 'uppercase', letterSpacing: '0.5px' });
const cardBody = { padding: '10px 12px' };
const lbl = { fontSize: '9.5px', fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', marginBottom: '3px' };

const PaymentAndReceiptDetails = () => {
  const { id } = useParams();
  const { isAuthenticated, companyId, userId, logout, token } = useAuth();

  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({});

  const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    const d = new Date(dateString);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  };

  useEffect(() => {
    if (isAuthenticated && tokenIsExpired(token)) { logout(); return; }
    setLoading(true);
    fetchData('PaymentAndReceiptApi', `/PaymentData/${id}`, token)
      .then(res => {
        if (res.responseCode === '1111') setFormData({ ...res.response_data, UpdatedBy: userId });
      })
      .catch(err => console.error('Error fetching details:', err))
      .finally(() => setLoading(false));
  }, [id]);

  return (
    <div style={{ fontFamily: 'Segoe UI, sans-serif', fontSize: '12px', width: '100%', padding: '6px', boxSizing: 'border-box', backgroundColor: C.bg }}>

      {/* ── Header ── */}
      <div style={{ ...card, overflow: 'visible' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', flexWrap: 'wrap', gap: '8px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <SwapHorizIcon style={{ fontSize: 16, color: C.accent }} />
            <span style={{ fontWeight: 800, fontSize: '13px', color: C.text, fontFamily: 'Inter, sans-serif' }}>Payment &amp; Receipt Details</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '12px', fontWeight: 600, color: '#3b82f6' }}>
              <input type="radio" name="IsType" value="1" checked={formData.IsType === true} readOnly disabled /> Payment
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '12px', fontWeight: 600, color: '#a855f7' }}>
              <input type="radio" name="IsType" value="0" checked={formData.IsType === false} readOnly disabled /> Receipt
            </label>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <Link to={{ pathname: '/PaymentAndReceiptIndex', state: { from: '/PaymentAndReceiptDetails' } }} onClick={() => localStorage.setItem('shouldReload', 'true')}
              style={{ height: '28px', padding: '0 14px', borderRadius: '7px', border: `1px solid ${C.cardBorder}`, backgroundColor: C.card, color: C.muted, fontWeight: 600, fontSize: '11px', display: 'flex', alignItems: 'center', textDecoration: 'none' }}>
              ← Back
            </Link>
          </div>
        </div>
      </div>

      {loading && (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
          <CircularProgress size={24} sx={{ color: C.accent }} />
        </div>
      )}

      {/* ── Entry Details ── */}
      <div style={card}>
        <div style={cardHead()}>📋 Entry Details</div>
        <div style={{ ...cardBody, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '10px' }}>
          <div style={{ gridColumn: 'span 2' }}>
            <label style={lbl}>Master</label>
            <input type="text" value={formData.MasterName || ''} readOnly style={inp} />
          </div>
          <div>
            <label style={lbl}>Date</label>
            <input type="date" value={formatDateForInput(formData.BillDate)} readOnly style={inp} />
          </div>
          <div>
            <label style={lbl}>Bill Number</label>
            <input type="text" value={`${formData.BillNumber || ''}/${formData.FinYear || ''}`} readOnly style={inp} />
          </div>
          <div>
            <label style={lbl}>Amount</label>
            <input type="number" value={formData.Amount || ''} readOnly style={inpR} />
          </div>
          <div>
            <label style={{ ...lbl, display: 'flex', alignItems: 'center', gap: '6px' }}>
              GST % <input type="checkbox" checked={formData.IsIGST || false} readOnly disabled />
              <span style={{ color: C.danger, fontSize: '9px' }}>IGST</span>
            </label>
            <select value={formData.GSTRate || 0} disabled style={{ ...inp, cursor: 'default' }}>
              {[0, 3, 5, 12, 18, 28].map(v => <option key={v} value={v}>{v}</option>)}
            </select>
          </div>
          <div>
            <label style={lbl}>Remarks</label>
            <input type="text" value={formData.Remarks || ''} readOnly style={inp} />
          </div>
        </div>
        {/* GST summary bar */}
        <div style={{ display: 'flex', gap: '20px', padding: '6px 12px', borderTop: `1px solid ${C.cardBorder}`, backgroundColor: '#f8fafa', flexWrap: 'wrap' }}>
          {[['CGST', formData.CGSTAmount], ['SGST', formData.SGSTAmount], ['IGST', formData.IGSTAmount], ['R.Off', formData.RoundOff], ['Total', formData.TotalAmount]].map(([label, val]) => (
            <div key={label} style={{ fontSize: '11px', color: C.muted }}>{label}: <strong style={{ color: '#B71C1C' }}>{val}</strong></div>
          ))}
        </div>
      </div>

      {/* ── Payment Details ── */}
      <div style={card}>
        <div style={cardHead('#f59e0b')}>💳 Payment Details</div>
        <div style={{ ...cardBody, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '10px' }}>
          <div>
            <label style={lbl}>Payment Mode</label>
            <input type="text" value={formData.PaymentName || ''} readOnly style={{ ...inp, color: '#b45309', fontWeight: 700 }} />
          </div>
          <div>
            <label style={lbl}>Bank</label>
            <input type="text" value={formData.BankName || ''} readOnly style={{ ...inp, color: '#b45309', fontWeight: 700 }} />
          </div>
          <div>
            <label style={lbl}>Reference No</label>
            <input type="text" value={formData.ChequeNumber || ''} readOnly style={inp} />
          </div>
          <div>
            <label style={lbl}>Reference Date</label>
            <input type="date" value={formatDateForInput(formData.ChequeDate)} readOnly style={inp} />
          </div>
          <div>
            <label style={lbl}>Reference Details</label>
            <input type="text" value={formData.chequeDetails || ''} readOnly style={inp} />
          </div>
        </div>
      </div>

    </div>
  );
};

export default PaymentAndReceiptDetails;
